import { useEffect, useState, Fragment } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney } from "@/components/Shared";
import { CheckCircle, Warning, CaretDown, CaretUp, ArrowsClockwise, CloudArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

// React.Fragment doesn't take a `key` prop by default in the short syntax,
// so we use the long form when we need to map a list of fragments with keys.
const FragmentWithKey = Fragment;

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function SquareReconciliation({ lang = "es" }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // month index 1-12
  const [detail, setDetail] = useState({}); // {month: detail_obj}
  const [reloading, setReloading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const load = async (y = year, refresh = false) => {
    if (refresh) setReloading(true); else setLoading(true);
    try {
      const [{ data }, statusRes] = await Promise.all([
        api.get(`/square/reconciliation?year=${y}`),
        api.get("/square/auto-sync-status").catch(() => ({ data: null })),
      ]);
      setData(data);
      if (statusRes?.data) setSyncStatus(statusRes.data);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const toggleDetail = async (m) => {
    if (expanded === m) {
      setExpanded(null);
      return;
    }
    setExpanded(m);
    if (!detail[m]) {
      try {
        const { data } = await api.get(`/square/reconciliation/month-detail?year=${year}&month=${m}`);
        setDetail((d) => ({ ...d, [m]: data }));
      } catch {
        setDetail((d) => ({ ...d, [m]: { square_paid: [], manual_paid: [] } }));
      }
    }
  };

  const importMissing = async (m) => {
    if (!confirm(`Importar pagos faltantes de Square para ${MONTHS[m - 1]} ${year}?\n\nSólo se crearán facturas para pagos COMPLETED que NO existan ya en el Dashboard. Idempotente.`)) return;
    const tid = toast.loading("Importando faltantes…");
    try {
      const { data } = await api.post(`/square/backfill-missing?year=${year}&month=${m}`);
      toast.dismiss(tid);
      if (data.invoices_created === 0) {
        toast.success(`✓ ${MONTHS[m - 1]} ya estaba al día (${data.payments_in_square} pagos verificados)`);
      } else {
        toast.success(
          `${data.invoices_created} factura(s) importada(s)${data.stub_clients_created ? ` · ${data.stub_clients_created} stub clients` : ""}`,
          { duration: 6000 }
        );
      }
      // Refresh month detail and the whole year
      setDetail((d) => { const c = { ...d }; delete c[m]; return c; });
      await load(year, true);
      // Re-open detail to show the updated data
      try {
        const { data: detail2 } = await api.get(`/square/reconciliation/month-detail?year=${year}&month=${m}`);
        setDetail((d) => ({ ...d, [m]: detail2 }));
      } catch { /* noop */ }
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiError(e.response?.data?.detail) || "Error al importar");
    }
  };

  const MONTHS = lang === "en"
    ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    : MONTHS_ES;

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6 shadow-sm">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-semibold mb-1">
          {lang === "en" ? "Reconciliation" : "Reconciliación"}
        </div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">Square ↔ Dashboard</h3>
        <div className="text-sm text-slate-500 dark:text-slate-400">{lang === "en" ? "Loading…" : "Cargando…"}</div>
      </div>
    );
  }

  const t = data?.totals || { square: 0, dashboard: 0, delta: 0, matches: true };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6 shadow-sm" data-testid="square-reconciliation">
      <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-semibold mb-1">
            {lang === "en" ? "Reconciliation" : "Reconciliación"}
          </div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">Square ↔ Dashboard</h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 px-2 py-1.5 rounded font-mono h-8"
            data-testid="recon-year"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() => load(year, true)}
            disabled={reloading}
            data-testid="recon-refresh"
            title={lang === "en" ? "Refresh" : "Actualizar"}
            className="inline-flex items-center justify-center w-8 h-8 border border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 rounded text-slate-500 dark:text-slate-300 transition-colors"
          >
            <ArrowsClockwise size={13} className={reloading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 max-w-2xl">
        {lang === "en"
          ? "Compare Square's transactions against the Dashboard's official records. Useful for year-end tax reconciliation: if Square reports $X on the 1099-K, your Dashboard should match."
          : "Compara las transacciones de Square con los registros oficiales del Dashboard. Útil para el cierre fiscal: si Square reporta $X en el 1099-K, tu Dashboard debe coincidir."}
      </p>

      {/* Auto-sync status pill */}
      {syncStatus && syncStatus.completed_at && (
        <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 mb-4 px-2.5 py-1 border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 rounded text-[10px] uppercase tracking-[0.15em] font-semibold text-emerald-800 dark:text-emerald-300 max-w-full">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {lang === "en" ? "Auto-sync" : "Auto-sync"}
          </span>
          <span className="text-emerald-700 dark:text-emerald-400 font-mono normal-case tracking-normal break-all">
            {syncStatus.completed_at?.replace("T", " ").slice(0, 16)}Z · {syncStatus.payments_scanned ?? 0} {lang === "en" ? "scanned" : "verificados"} · {syncStatus.invoices_created ?? 0} {lang === "en" ? "new" : "nuevos"}
          </span>
        </div>
      )}

      {/* Year totals strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="border border-slate-200 dark:border-slate-800 rounded-md px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Square {year}</div>
          <div className="font-mono text-lg text-slate-900 dark:text-slate-100 mt-1 break-all" data-testid="recon-total-square">{fmtMoney(t.square)}</div>
        </div>
        <div className="border border-slate-200 dark:border-slate-800 rounded-md px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Dashboard {year}</div>
          <div className="font-mono text-lg text-slate-900 dark:text-slate-100 mt-1 break-all" data-testid="recon-total-dashboard">{fmtMoney(t.dashboard)}</div>
        </div>
        <div className={`border rounded-md px-4 py-3 ${
          t.matches
            ? "border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800"
            : "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700"
        }`}>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            {t.matches
              ? <><CheckCircle size={12} weight="fill" className="text-emerald-600 dark:text-emerald-400" /> {lang === "en" ? "Match" : "Cuadra"}</>
              : <><Warning size={12} weight="fill" className="text-amber-600 dark:text-amber-400" /> {lang === "en" ? "Difference" : "Diferencia"}</>
            }
          </div>
          <div className="font-mono text-lg text-slate-900 dark:text-slate-100 mt-1" data-testid="recon-total-delta">
            {t.delta >= 0 ? "+" : ""}{fmtMoney(t.delta)}
          </div>
        </div>
      </div>

      {/* Monthly grid */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/40">
            <tr className="text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 w-24">{lang === "en" ? "Month" : "Mes"}</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 text-right">Square</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 text-right">Dashboard</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 text-right w-32">Δ</th>
              <th className="px-3 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.months.map((m) => {
              const hasMovement = m.square_total > 0 || m.dashboard_total > 0;
              const isOpen = expanded === m.month;
              return (
                <FragmentWithKey key={m.month}>
                  <tr
                    onClick={() => hasMovement && toggleDetail(m.month)}
                    className={`${hasMovement ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40" : "opacity-50"} transition-colors`}
                    data-testid={`recon-row-${m.month}`}
                  >
                    <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-slate-100">{MONTHS[m.month - 1]}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">{fmtMoney(m.square_total)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">{fmtMoney(m.dashboard_total)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${
                      m.matches
                        ? "text-emerald-700 dark:text-emerald-400"
                        : m.delta > 0
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-red-700 dark:text-red-400"
                    }`}>
                      {m.matches ? "✓" : `${m.delta >= 0 ? "+" : ""}${fmtMoney(m.delta)}`}
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">
                      {hasMovement && (isOpen ? <CaretUp size={12} /> : <CaretDown size={12} />)}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/40 dark:bg-slate-800/20">
                      <td colSpan={5} className="px-3 py-3">
                        <MonthDetail
                          d={detail[m.month]}
                          lang={lang}
                          delta={m.delta}
                          onImportMissing={() => importMissing(m.month)}
                        />
                      </td>
                    </tr>
                  )}
                </FragmentWithKey>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-2 text-right">
        {lang === "en" ? "Computed at" : "Calculado el"} {data.computed_at?.replace("T", " ").slice(0, 19)}
      </div>
    </div>
  );
}

function MonthDetail({ d, lang, delta, onImportMissing }) {
  if (!d) return <div className="text-xs text-slate-500 dark:text-slate-400">Cargando…</div>;
  const SqLabel = `Square (${d.square_paid.length})`;
  const MaLabel = lang === "en" ? `Manual / Cash (${d.manual_paid.length})` : `Manual / Efectivo (${d.manual_paid.length})`;
  // If delta is NEGATIVE → Square has more $$ than the dashboard (missing imports)
  const showImportBtn = delta < 0;
  return (
    <div>
      {showImportBtn && (
        <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
          <div className="text-xs text-amber-900 dark:text-amber-200">
            <Warning size={13} weight="fill" className="inline mr-1 -mt-0.5" />
            {lang === "en"
              ? `Square received ${fmtMoney(Math.abs(delta))} more than this dashboard records. Likely missed webhooks.`
              : `Square recibió ${fmtMoney(Math.abs(delta))} más que lo registrado aquí. Probablemente webhooks que fallaron.`}
          </div>
          <button
            onClick={onImportMissing}
            data-testid="import-missing-month"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium bg-slate-900 hover:bg-slate-800 text-white rounded transition-colors whitespace-nowrap"
          >
            <CloudArrowDown size={12} weight="bold" />
            {lang === "en" ? "Import missing" : "Importar faltantes"}
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 mb-2">{SqLabel}</div>
          {d.square_paid.length === 0
            ? <div className="text-xs text-slate-400 dark:text-slate-500">—</div>
            : (
              <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
                {d.square_paid.map((i) => (
                  <li key={i.id} className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-700 dark:text-slate-300 truncate">{i.client_name}</span>
                    <span className="font-mono text-slate-900 dark:text-slate-100 whitespace-nowrap">{fmtMoney(i.amount)}</span>
                  </li>
                ))}
              </ul>
            )
          }
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 mb-2">{MaLabel}</div>
          {d.manual_paid.length === 0
            ? <div className="text-xs text-slate-400 dark:text-slate-500">—</div>
            : (
              <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
                {d.manual_paid.map((i) => (
                  <li key={i.id} className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-700 dark:text-slate-300 truncate">{i.client_name}</span>
                    <span className="font-mono text-slate-900 dark:text-slate-100 whitespace-nowrap">{fmtMoney(i.amount)}</span>
                  </li>
                ))}
              </ul>
            )
          }
        </div>
      </div>
    </div>
  );
}
